import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "../../../components/ui/statusBadge/StatusBadge";
import { PrioBadge } from "../../../components/ui/prioBadge/PrioBadge";
import { EmptyState } from "../../../components/ui/emptyState/EmptyState";
import { useModal } from "../../../components/ui/modal/ModalProvider";
import { useToast } from "../../../components/ui/toast/ToastProvider";
import { useIncidentDetail } from "../hooks/useIncidentDetail";
import { useHelpRequestMessages } from "../hooks/useHelpRequestMessages";
import { useSupportPeople } from "../hooks/useSupportPeople";
import { AssignForm, ModalActions, PriorityForm, StatusForm } from "./TicketModals";
import {
  AUDIT_EVENT_TYPE,
  AUDIT_EVENT_TYPE_META,
  HELP_REQUEST_STATUS,
  MESSAGE_SENDER_ROLE,
  OPEN_HELP_REQUEST_STATUSES,
  SUPPORT_ENTITY_TYPE,
  helpRequestStatusLabel,
  incidentPriorityLabel,
  supportEntityTypeLabel,
} from "../../../constants/supportStatus";
import { formatGraphqlError, getGraphqlError } from "../../../utils/graphqlError";
import { datetime, relative } from "../../../utils/format";

const byDate = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
const initials = (name = "") => name.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const transition = (from, to) => (from ? `${from} → ${to}` : to);

export function TicketFiche({ id }) {
  const navigate = useNavigate();
  const { openModal, confirm } = useModal();
  const { toast } = useToast();
  const { myId, personName, assigneeOptions } = useSupportPeople();
  const { incident, request, comments, events, loading, error, assign, changePriority, changeStatus, addComment, isMutating, isCommenting } =
    useIncidentDetail(id);
  const { messages, sendMessage, isSending } = useHelpRequestMessages(incident?.requestId || request?.id);
  const [chatInput, setChatInput] = useState("");
  const [commentInput, setCommentInput] = useState("");

  if (loading && !incident) return <div className="card"><div className="card-empty">Chargement du ticket…</div></div>;
  if (error && !incident) {
    const { status } = getGraphqlError(error);
    return <EmptyState title={status === 404 ? "Ticket introuvable" : "Impossible de charger le ticket"} sub={formatGraphqlError(error)} />;
  }
  if (!incident) return <EmptyState title="Ticket introuvable" sub={id} />;

  const status = request?.status;
  const isOpen = OPEN_HELP_REQUEST_STATUSES.includes(status);
  const requesterLabel = personName(request?.requesterId) || "Demandeur";

  const run = async (action, successMsg) => {
    try {
      await action();
      toast(successMsg, "success");
      return true;
    } catch (e) {
      toast(formatGraphqlError(e), "error", { duration: 6000 });
      return false;
    }
  };

  const onSendMessage = async () => {
    const txt = chatInput.trim();
    if (!txt) return;
    if (await run(() => sendMessage(txt), "Message envoyé")) setChatInput("");
  };

  const onAddComment = async () => {
    const txt = commentInput.trim();
    if (!txt) return;
    if (await run(() => addComment(txt), "Note interne ajoutée")) setCommentInput("");
  };

  const openChangeStatus = () => {
    const valuesRef = { current: null };
    openModal({
      title: "Changer le statut",
      body: <StatusForm valuesRef={valuesRef} currentStatus={status} />,
      footer: (
        <ModalActions
          submitLabel="Enregistrer"
          testId="ok-status"
          onSubmit={async () => {
            await changeStatus(valuesRef.current.status, valuesRef.current.resolutionComment.trim());
            toast("Statut mis à jour", "success");
          }}
        />
      ),
    });
  };

  const openChangePriority = () => {
    const valuesRef = { current: null };
    openModal({
      title: "Changer la priorité",
      body: <PriorityForm valuesRef={valuesRef} currentPriority={incident.priority} />,
      footer: (
        <ModalActions
          submitLabel="Enregistrer"
          testId="ok-priority"
          onSubmit={async () => {
            await changePriority(valuesRef.current.priority);
            toast("Priorité mise à jour", "success");
          }}
        />
      ),
    });
  };

  const openAssign = () => {
    const valuesRef = { current: null };
    openModal({
      title: "Réassigner le ticket",
      body: <AssignForm valuesRef={valuesRef} currentAssigneeId={incident.assigneeId} assigneeOptions={assigneeOptions} />,
      footer: (
        <ModalActions
          submitLabel="Réassigner"
          testId="ok-assign"
          onSubmit={async () => {
            if (!valuesRef.current.assigneeId) throw new Error("Sélectionnez un agent");
            await assign(valuesRef.current.assigneeId);
            toast("Ticket réassigné", "success");
          }}
        />
      ),
    });
  };

  const resolve = () => {
    confirm({
      title: "Marquer comme résolu",
      message: "Le ticket sera clôturé et le demandeur notifié.",
      confirmText: "Résoudre",
      type: "success",
      onConfirm: (comment) => run(() => changeStatus(HELP_REQUEST_STATUS.RESOLVED, comment?.trim()), "Ticket résolu"),
    });
  };

  const eventLabel = (e) => {
    switch (e.eventType) {
      case AUDIT_EVENT_TYPE.STATUS_CHANGED:
        return `Statut : ${transition(e.previousValue && helpRequestStatusLabel(e.previousValue), helpRequestStatusLabel(e.newValue))}`;
      case AUDIT_EVENT_TYPE.PRIORITY_CHANGED:
        return `Priorité : ${transition(e.previousValue && incidentPriorityLabel(e.previousValue), incidentPriorityLabel(e.newValue))}`;
      case AUDIT_EVENT_TYPE.ASSIGNEE_CHANGED:
        return e.newValue ? `Assigné à ${personName(e.newValue)}` : "Assignation retirée";
      default:
        return AUDIT_EVENT_TYPE_META[e.eventType]?.label || e.eventType;
    }
  };

  const sortedMessages = [...messages].sort(byDate);
  const sortedComments = [...comments].sort(byDate);
  const sortedEvents = [...events].sort(byDate);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><a onClick={() => navigate("/tickets")}>Tickets</a><i className="fa-solid fa-chevron-right"></i>{incident.incidentRef}</div>
          <h1 className="page-title">{request?.subject || incident.incidentRef}</h1>
          <div className="page-sub">
            <span className="mono">{incident.incidentRef}</span>
            {request?.helpRequestRef ? <> · demande <span className="mono">{request.helpRequestRef}</span></> : null}
            {" "}· ouvert par {requesterLabel} · {relative(request?.createdAt || incident.createdAt)}
          </div>
        </div>
        <div className="page-actions">
          <PrioBadge priorite={incident.priority} />
          {status ? <StatusBadge statut={status} domain="support" /> : null}
          <button className="btn" data-testid="btn-change-priority" onClick={openChangePriority} disabled={isMutating}><i className="fa-solid fa-signal"></i> Priorité</button>
          <button className="btn" data-testid="btn-change-status" onClick={openChangeStatus} disabled={isMutating}><i className="fa-solid fa-flag"></i> Changer le statut</button>
        </div>
      </div>

      <div className="detail-grid">
        <div>
          {request ? (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div><h3 className="card-title">Demande initiale</h3><div className="card-sub">{requesterLabel} · {datetime(request.createdAt)}</div></div>
              </div>
              <div className="card-body">
                <div style={{ whiteSpace: "pre-wrap" }}>{request.content}</div>
                {request.attachments?.length ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                    {request.attachments.map((url, i) => (
                      <a key={url} className="badge outline" href={url} target="_blank" rel="noreferrer"><i className="fa-solid fa-paperclip"></i> Pièce jointe {i + 1}</a>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div><h3 className="card-title">Conversation</h3><div className="card-sub">{sortedMessages.length} message{sortedMessages.length > 1 ? "s" : ""}</div></div>
            </div>
            <div className="chat" data-testid="chat-thread">
              {sortedMessages.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>Aucun message pour le moment. Commencez la conversation ci-dessous.</div>
              ) : (
                sortedMessages.map((m) => {
                  const fromAgent = m.senderRole === MESSAGE_SENDER_ROLE.SUPPORT_AGENT;
                  const author = fromAgent ? personName(m.senderId) || "Support" : requesterLabel;
                  return (
                    <div className={`msg${fromAgent ? " me" : ""}`} key={m.id}>
                      <div className="avatar">{initials(author)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="bubble" style={{ whiteSpace: "pre-wrap" }}>{m.content}</div>
                        <div className="meta">{author} · {datetime(m.createdAt)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="chat-composer">
              <textarea
                data-testid="chat-input"
                placeholder={`Écrire une réponse à ${requesterLabel}…`}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
              ></textarea>
              <button className="btn btn-primary btn-sm" data-testid="chat-send" onClick={onSendMessage} disabled={isSending || !chatInput.trim()}>
                <i className={`fa-solid ${isSending ? "fa-spinner fa-spin" : "fa-paper-plane"}`}></i>
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div><h3 className="card-title">Notes internes</h3><div className="card-sub">{sortedComments.length} note{sortedComments.length > 1 ? "s" : ""}</div></div>
              <span className="badge outline"><i className="fa-solid fa-eye-slash"></i> Non visibles du demandeur</span>
            </div>
            <div className="chat" data-testid="comments-thread">
              {sortedComments.length === 0 ? (
                <div className="muted" style={{ padding: 16, textAlign: "center" }}>Aucune note interne.</div>
              ) : (
                sortedComments.map((c) => {
                  const author = personName(c.senderId) || "Agent";
                  return (
                    <div className="msg" key={c.id}>
                      <div className="avatar">{initials(author)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="bubble" style={{ whiteSpace: "pre-wrap" }}>{c.content}</div>
                        <div className="meta">{author} · {datetime(c.createdAt)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="chat-composer">
              <textarea
                data-testid="comment-input"
                placeholder="Ajouter une note pour l'équipe support…"
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
              ></textarea>
              <button className="btn btn-sm" data-testid="comment-send" onClick={onAddComment} disabled={isCommenting || !commentInput.trim()}>
                <i className={`fa-solid ${isCommenting ? "fa-spinner fa-spin" : "fa-plus"}`}></i>
              </button>
            </div>
          </div>
        </div>

        <aside>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Informations</h3></div>
            <div className="card-body">
              <div className="info-list" style={{ gridTemplateColumns: "1fr" }}>
                <div>
                  <div className="k">{supportEntityTypeLabel(request?.entityType)}</div>
                  <div className="v">
                    {request?.entityType === SUPPORT_ENTITY_TYPE.ESTABLISHMENT && request.entityId ? (
                      <a onClick={() => navigate("/etablissements/" + request.entityId)} style={{ color: "var(--primary)", cursor: "pointer" }}>{request.entityId}</a>
                    ) : (
                      <span className="mono">{request?.entityId || "—"}</span>
                    )}
                  </div>
                </div>
                <div><div className="k">Demandeur</div><div className="v">{requesterLabel}</div></div>
                <div><div className="k">Agent responsable</div><div className="v">{personName(incident.assigneeId) || <span className="badge outline">Non assigné</span>}</div></div>
                <div><div className="k">Créé le</div><div className="v">{datetime(incident.createdAt)}</div></div>
                <div><div className="k">Dernière activité</div><div className="v">{relative(incident.updatedAt || incident.createdAt)}</div></div>
              </div>
              <div className="hr"></div>
              <button className="btn" style={{ width: "100%", marginBottom: 8 }} data-testid="btn-assign" onClick={openAssign} disabled={isMutating}><i className="fa-solid fa-user-plus"></i> Réassigner</button>
              {myId && incident.assigneeId !== myId ? (
                <button className="btn" style={{ width: "100%", marginBottom: 8 }} data-testid="btn-assign-me" onClick={() => run(() => assign(myId), "Ticket assigné")} disabled={isMutating}>
                  <i className="fa-solid fa-hand"></i> M'assigner
                </button>
              ) : null}
              {isOpen ? (
                <button className="btn btn-success" style={{ width: "100%" }} data-testid="btn-resolve" onClick={resolve} disabled={isMutating}><i className="fa-solid fa-check"></i> Marquer comme résolu</button>
              ) : null}
            </div>
          </div>

          <div style={{ height: 16 }}></div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">Historique</h3></div>
            <div className="card-body">
              <div className="timeline">
                {sortedEvents.length === 0 ? (
                  <div className="muted">Aucun changement enregistré.</div>
                ) : (
                  sortedEvents.map((e) => (
                    <div className="tl-item done" key={e.id}>
                      <div className="tl-time">{datetime(e.createdAt)}{e.actorId ? ` · ${personName(e.actorId)}` : ""}</div>
                      <div className="tl-title">{eventLabel(e)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

export default TicketFiche;
